import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  CalendarAdminService,
  AdminCalendarEvent
} from '../../../services/calendar/calendar.service';

@Component({
  selector: 'app-calendar-events',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DragDropModule],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.css'
})

export class CalendarComponent implements OnInit {
  viewDate = new Date();
  today = new Date();

  loading = false;
  loadingDayEvents = false;
  saving = false;

  selectedDate: Date | null = null;

  markedDays = new Set<number>();
  eventCounts: Record<number, number> = {};

  days: number[] = [];
  blanks: number[] = [];

  showDayPopup = false;
  showFormModal = false;
  editingItemId: number | null = null;

  dayEvents: AdminCalendarEvent[] = [];

  form: FormGroup = this.fb.group({
    start_date: ['', Validators.required],
    end_date: [''],
    title_ka: [''],
    title_en: [''],
    description_ka: [''],
    description_en: [''],
    url: [''],
    target: ['_self'],
    color: ['red'],
    sort_order: [0],
    is_active: [true]
  });

  colorOptions = [
    { label: 'Red', value: 'წითელი' },
    { label: 'Blue', value: 'ლურჯი' },
    { label: 'Green', value: 'მწვანე' },
    { label: 'Orange', value: 'ნარინჯისფერი' },
    { label: 'Purple', value: 'იისფერი' }
  ];

  constructor(
    private fb: FormBuilder,
    private service: CalendarAdminService
  ) {}

  ngOnInit(): void {
    this.buildCalendar();
    this.loadMonthDays();
  }

  buildCalendar() {
    const y = this.viewDate.getFullYear();
    const m = this.viewDate.getMonth();

    const firstDay = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();

    const offset = firstDay === 0 ? 6 : firstDay - 1;

    this.blanks = Array(offset).fill(0);
    this.days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  }

  toMonth(d: Date) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  toDate(d: Date) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  monthLabel() {
    return this.viewDate.toLocaleDateString('ka-GE', {
      month: 'long',
      year: 'numeric'
    });
  }

  prevMonth() {
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1);
    this.buildCalendar();
    this.loadMonthDays();
  }

  nextMonth() {
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 1);
    this.buildCalendar();
    this.loadMonthDays();
  }

  hasEvent(day: number) {
    return this.markedDays.has(day);
  }

  eventCount(day: number): number {
    return this.eventCounts[day] || 0;
  }

  isSelected(day: number): boolean {
    if (!this.selectedDate) return false;
    return (
      this.selectedDate.getFullYear() === this.viewDate.getFullYear() &&
      this.selectedDate.getMonth() === this.viewDate.getMonth() &&
      this.selectedDate.getDate() === day
    );
  }

  loadMonthDays() {
    const month = this.toMonth(this.viewDate);
    this.loading = true;

    this.service.getEventDays(month)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          this.markedDays = new Set(res.days || []);
          this.eventCounts = res.counts || {};
        },
        error: () => {
          this.markedDays = new Set();
          this.eventCounts = {};
        }
      });
  }

  openDay(day: number, ev?: Event) {
    ev?.preventDefault();
    ev?.stopPropagation();

    const selected = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth(), day);
    this.selectedDate = selected;
    this.showDayPopup = true;
    this.loadDayEvents();
  }

  closeDayPopup() {
    this.showDayPopup = false;
  }

  loadDayEvents() {
    if (!this.selectedDate) return;

    const date = this.toDate(this.selectedDate);
    this.loadingDayEvents = true;

    this.service.list({
      date_from: date,
      date_to: date
    })
    .pipe(finalize(() => (this.loadingDayEvents = false)))
    .subscribe({
      next: (res) => {
        this.dayEvents = res.items?.data || [];
      },
      error: () => {
        this.dayEvents = [];
      }
    });
  }

  openCreateModal() {
    const date = this.selectedDate ? this.toDate(this.selectedDate) : this.toDate(new Date());

    this.editingItemId = null;
    this.form.reset({
      start_date: date,
      end_date: date,
      title_ka: '',
      title_en: '',
      description_ka: '',
      description_en: '',
      url: '',
      target: '_self',
      color: 'red',
      sort_order: 0,
      is_active: true
    });

    this.showFormModal = true;
  }

  openEditModal(item: AdminCalendarEvent) {
    this.editingItemId = item.id;
    this.form.reset({
      start_date: item.start_date,
      end_date: item.end_date || item.start_date,
      title_ka: item.title_ka || '',
      title_en: item.title_en || '',
      description_ka: item.description_ka || '',
      description_en: item.description_en || '',
      url: item.url || '',
      target: item.target || '_self',
      color: item.color || 'red',
      sort_order: item.sort_order || 0,
      is_active: !!item.is_active
    });

    this.showFormModal = true;
  }

  closeFormModal() {
    this.showFormModal = false;
    this.editingItemId = null;
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = this.form.getRawValue();
    this.saving = true;

    const req = this.editingItemId
      ? this.service.update(this.editingItemId, payload)
      : this.service.create(payload);

    req.pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.closeFormModal();
          this.loadMonthDays();
          this.loadDayEvents();
        }
      });
  }

  remove(item: AdminCalendarEvent) {
    if (!confirm('ნამდვილად გსურთ წაშლა?')) return;

    this.service.delete(item.id).subscribe({
      next: () => {
        this.loadMonthDays();
        this.loadDayEvents();
      }
    });
  }

  drop(event: CdkDragDrop<AdminCalendarEvent[]>) {
    moveItemInArray(this.dayEvents, event.previousIndex, event.currentIndex);

    const items = this.dayEvents.map((item, index) => ({
      id: item.id,
      sort_order: index + 1
    }));

    this.dayEvents = this.dayEvents.map((item, index) => ({
      ...item,
      sort_order: index + 1
    }));

    this.service.reorder(items).subscribe();
  }
}